/**
 * Patient Context Bar API
 *
 * RTK Query endpoints for fetching patient context data
 */

import { baseApi } from '@store/baseApi';

import type {
  PatientModuleDataResponse,
  PatientPreRecordingInfoResponse,
  TreatmentPlanApiResponse,
} from '../types';

const PATIENT_CONTEXT_TAG_TYPES = [
  'PatientContext',
  'PatientModuleData',
  'PatientPreRecordingInfo',
  'PatientTreatmentPlan',
] as const;

/**
 * Patient Context Bar API
 * Extends baseApi with patient context endpoints
 */
export const patientContextBarApi = baseApi
  .enhanceEndpoints({ addTagTypes: PATIENT_CONTEXT_TAG_TYPES })
  .injectEndpoints({
    endpoints: builder => ({
      /**
       * Get patient module data (medications, diagnoses, allergies)
       */
      getPatientModuleData: builder.query<PatientModuleDataResponse, { patientId: string }>({
        query: ({ patientId }) => ({
          url: `/patient/getPatientData?patient_id=${patientId}`,
          method: 'GET',
        }),
        providesTags: (result, error, { patientId }) => [
          { type: 'PatientModuleData', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),

      /**
       * Get patient pre-recording information (last encounter, visit summary)
       */
      getPatientPreRecordingInfo: builder.query<
        PatientPreRecordingInfoResponse,
        { patientId: string }
      >({
        query: ({ patientId }) => ({
          url: `/patient/getPreRecordingInformation?patient_id=${patientId}`,
          method: 'GET',
        }),
        providesTags: (result, error, { patientId }) => [
          { type: 'PatientPreRecordingInfo', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),

      /**
       * Update patient module data (generic endpoint for medications, diagnoses, etc.)
       */
      updatePatientModule: builder.mutation<
        void,
        {
          patientId: string;
          patientModuleId: string;
          dataKey: string;
          data: any[];
        }
      >({
        query: ({ patientId, patientModuleId, dataKey, data }) => ({
          url: '/patient/updatePatientModule',
          method: 'POST',
          body: {
            patient_id: patientId,
            patient_module_id: patientModuleId,
            data: { [dataKey]: data },
          },
        }),
        invalidatesTags: (result, error, { patientId }) => [
          { type: 'PatientModuleData', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),

      /**
       * Get treatment plan for a patient
       * Returns null for 404 (no treatment plan exists)
       */
      getTreatmentPlan: builder.query<TreatmentPlanApiResponse | null, { patientId: string }>({
        queryFn: async ({ patientId }, _queryApi, _extraOptions, baseQuery) => {
          const result = await baseQuery({
            url: `/treatment_plan/getTreatmentPlan?patient_id=${patientId}`,
            method: 'GET',
          });
          if (result.error) {
            // 404 means no treatment plan — return null data instead of error
            const status = 'status' in result.error ? result.error.status : null;
            if (status === 404) {
              return { data: null };
            }
            return { error: result.error };
          }
          const response = result.data as { treatment_plan: TreatmentPlanApiResponse };
          return { data: response.treatment_plan };
        },
        providesTags: (result, error, { patientId }) => [
          { type: 'PatientTreatmentPlan', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),

      /**
       * Create or update a treatment plan
       */
      saveTreatmentPlan: builder.mutation<
        TreatmentPlanApiResponse,
        {
          patientId: string;
          markdownContent: string;
          treatmentPlanId?: string | null;
        }
      >({
        query: ({ patientId, markdownContent, treatmentPlanId }) => {
          if (treatmentPlanId) {
            return {
              url: '/treatment_plan/updateTreatmentPlan',
              method: 'POST',
              body: {
                patient_id: patientId,
                treatment_plan_id: treatmentPlanId,
                markdown_content: markdownContent,
              },
            };
          }
          return {
            url: '/treatment_plan/createTreatmentPlan',
            method: 'POST',
            body: {
              patient_id: patientId,
              markdown_content: markdownContent,
            },
          };
        },
        invalidatesTags: (result, error, { patientId }) => [
          { type: 'PatientTreatmentPlan', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),
    }),
    overrideExisting: false,
  });

// Export user-friendly hook names
export const {
  useGetPatientModuleDataQuery,
  useLazyGetPatientModuleDataQuery,
  useGetPatientPreRecordingInfoQuery,
  useLazyGetPatientPreRecordingInfoQuery,
  useUpdatePatientModuleMutation,
  useGetTreatmentPlanQuery,
  useSaveTreatmentPlanMutation,
} = patientContextBarApi;
